import { useMemo, useState, type FormEvent } from "react";
import { DataTable } from "../../components/DataTable";
import { StatusBadge } from "../../components/StatusBadge";
import { useAdminCompanies } from "../../api/admin";
import { extractErrorMessage } from "../../api/errors";
import {
  adminUserFilterParams,
  useAdminInviteUser,
  useAdminUsers,
  useAdminUserTotal,
  useUpdateUser,
  useDeleteUser,
  type AdminUser,
  type AdminUserFilters,
} from "../../api/users";
import { useDebouncedValue } from "../../hooks/useDebouncedValue";
import { formatRange, totalPagesFor, useCursorPaging } from "../../hooks/useCursorPaging";
import { UserPlus, Ban, CheckCircle2, Trash2, SquarePen } from "lucide-react";

const ROLE_OPTIONS = [
  { value: "viewer", label: "Viewer" },
  { value: "operator", label: "Operator" },
  { value: "tenant_admin", label: "Admin" },
];

const PAGE_SIZE_OPTIONS = [25, 50, 100];
const SEARCH_DEBOUNCE_MS = 300;
// Server limits on one request (see GET /admin/users).
const MAX_SEARCH_LENGTH = 100;
const MAX_FILTER_COMPANIES = 100;

// Every role, for filtering (inviting can't create a super admin).
const ROLE_FILTER_OPTIONS = [
  { value: "super_admin", label: "Super admin" },
  { value: "tenant_admin", label: "Admin" },
  { value: "operator", label: "Operator" },
  { value: "viewer", label: "Viewer" },
];

export function AdminUsersPage() {
  const companiesQuery = useAdminCompanies();
  const companiesData = companiesQuery.data;

  // Platform users scale with tenants, so the list is searched, filtered,
  // paged and counted on the server rather than loaded whole.
  const [filters, setFilters] = useState<AdminUserFilters>({});
  const [pageSize, setPageSize] = useState(PAGE_SIZE_OPTIONS[0]);
  const debouncedSearch = useDebouncedValue(filters.search ?? "", SEARCH_DEBOUNCE_MS);
  const serverFilters: AdminUserFilters = { ...filters, search: debouncedSearch };
  const paging = useCursorPaging(JSON.stringify([adminUserFilterParams(serverFilters).toString(), pageSize]));
  // More companies than one request may name: say so instead of sending a
  // request the server would reject.
  const tooManyCompanies = (serverFilters.tenantIds?.length ?? 0) > MAX_FILTER_COMPANIES;
  const { data, isPlaceholderData, isError, error: listError } = useAdminUsers({
    filters: serverFilters,
    limit: pageSize,
    cursor: paging.cursor,
    enabled: !tooManyCompanies,
  });
  const totalQuery = useAdminUserTotal(serverFilters, { enabled: !tooManyCompanies });
  const inviteUser = useAdminInviteUser();
  const updateUser = useUpdateUser();
  const deleteUser = useDeleteUser();
  const [showInvite, setShowInvite] = useState(false);
  const [tenantId, setTenantId] = useState("");
  const [email, setEmail] = useState("");
  const [firstName, setFirstName] = useState("");
  const [lastName, setLastName] = useState("");
  const [role, setRole] = useState("viewer");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState("");
  const [rowError, setRowError] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<null | { action: "disable" | "delete"; user: AdminUser }>(null);
  const [editing, setEditing] = useState<AdminUser | null>(null);
  const [editForm, setEditForm] = useState({ firstName: "", lastName: "", email: "", role: "viewer" });
  const [editError, setEditError] = useState<string | null>(null);
  const companies = useMemo(() => companiesData?.data ?? [], [companiesData?.data]);
  // The API returns tenantId only; name each row's company from the list.
  const companiesLoading = companiesQuery.isLoading;
  const users = useMemo(() => {
    if (tooManyCompanies) return [];
    const names = new Map(companies.map((company) => [company.tenantId, company.companyName]));
    return (data?.data ?? []).map((user) => ({
      ...user,
      companyName: names.get(user.tenantId) ?? (companiesLoading ? "…" : "Unknown company"),
    }));
  }, [companies, companiesLoading, data?.data, tooManyCompanies]);
  const hasMore = !!data?.pagination.hasMore;
  const total = totalQuery.isPlaceholderData ? undefined : totalQuery.data?.total;
  const totalIsLowerBound = !totalQuery.isPlaceholderData && !!totalQuery.data?.isLowerBound;

  function showNextPage() {
    const next = data?.pagination.cursor;
    if (next && !isPlaceholderData) paging.next(next, users.length);
  }
  const busy = updateUser.isPending || deleteUser.isPending;

  async function handleInvite(e: FormEvent) {
    e.preventDefault();
    setError("");
    setSuccess("");

    try {
      await inviteUser.mutateAsync({
        tenantId,
        email: email.trim(),
        role,
        firstName: firstName.trim(),
        lastName: lastName.trim(),
      });
      const companyName = companies.find((company) => company.tenantId === tenantId)?.companyName ?? "selected company";
      setSuccess(`Invite sent to ${email.trim()} for ${companyName}.`);
      setShowInvite(false);
      setTenantId("");
      setEmail("");
      setFirstName("");
      setLastName("");
      setRole("viewer");
    } catch (err) {
      setError(extractErrorMessage(err, "Failed to invite user"));
    }
  }

  async function changeRole(user: AdminUser, nextRole: string) {
    if (nextRole === user.role) return;
    setRowError(null);
    try {
      await updateUser.mutateAsync({ userId: user.userId, actingTenantId: user.tenantId, role: nextRole });
    } catch (err) {
      setRowError(extractErrorMessage(err, "Failed to update role"));
    }
  }

  async function setActive(user: AdminUser) {
    setRowError(null);
    try {
      await updateUser.mutateAsync({ userId: user.userId, actingTenantId: user.tenantId, status: "active" });
    } catch (err) {
      setRowError(extractErrorMessage(err, "Failed to enable user"));
    }
  }

  function openEdit(user: AdminUser) {
    setEditError(null);
    setEditForm({
      firstName: user.firstName,
      lastName: user.lastName,
      email: user.email,
      role: user.role,
    });
    setEditing(user);
  }

  async function saveEdit(e: FormEvent) {
    e.preventDefault();
    if (!editing) return;
    setEditError(null);
    try {
      await updateUser.mutateAsync({
        userId: editing.userId,
        actingTenantId: editing.tenantId,
        firstName: editForm.firstName,
        lastName: editForm.lastName,
        email: editForm.email,
        role: editForm.role,
      });
      setEditing(null);
    } catch (err) {
      setEditError(extractErrorMessage(err, "Failed to update user"));
    }
  }

  async function runConfirmedAction() {
    if (!confirm) return;
    const { action, user } = confirm;
    setRowError(null);
    try {
      if (action === "delete") {
        await deleteUser.mutateAsync({ userId: user.userId, actingTenantId: user.tenantId });
      } else {
        await updateUser.mutateAsync({ userId: user.userId, actingTenantId: user.tenantId, status: "deactivated" });
      }
    } catch (err) {
      setRowError(extractErrorMessage(err, action === "delete" ? "Failed to delete user" : "Failed to disable user"));
    } finally {
      setConfirm(null);
    }
  }

  const columns = [
    { key: "email", header: "Email", sortable: true },
    { key: "companyName", header: "Company", sortable: true },
    { key: "firstName", header: "First Name", sortable: true },
    { key: "lastName", header: "Last Name", sortable: true },
    {
      key: "role",
      header: "Role",
      sortable: true,
      render: (row: AdminUser) =>
        row.role === "super_admin" ? (
          <span className="capitalize">super admin</span>
        ) : (
          <select
            aria-label={`Role for ${row.email}`}
            value={row.role}
            disabled={busy}
            onChange={(e) => void changeRole(row, e.target.value)}
            className="rounded-md border border-gray-300 bg-white px-2 py-1 text-xs text-gray-900 disabled:opacity-50 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
          >
            {ROLE_OPTIONS.map((o) => (
              <option key={o.value} value={o.value}>{o.label}</option>
            ))}
          </select>
        ),
    },
    { key: "status", header: "Status", render: (row: AdminUser) => <StatusBadge status={row.status} /> },
    { key: "tenantId", header: "Tenant ID", render: (row: AdminUser) => <span className="font-mono text-xs">{row.tenantId.slice(0, 16)}</span> },
    {
      key: "actions",
      header: "",
      render: (row: AdminUser) =>
        row.role === "super_admin" ? null : (
          <div className="flex items-center justify-end gap-2">
            <button
              type="button"
              onClick={() => openEdit(row)}
              disabled={busy}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-gray-700 hover:bg-gray-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            >
              <SquarePen size={12} /> Edit
            </button>
            {row.status === "deactivated" ? (
              <button
                type="button"
                onClick={() => void setActive(row)}
                disabled={busy}
                className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-emerald-700 hover:bg-emerald-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-emerald-300 dark:hover:bg-riot-navy-700"
              >
                <CheckCircle2 size={12} /> Enable
              </button>
            ) : (
              <button
                type="button"
                onClick={() => setConfirm({ action: "disable", user: row })}
                disabled={busy}
                className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-orange-700 hover:bg-orange-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-orange-300 dark:hover:bg-riot-navy-700"
              >
                <Ban size={12} /> Disable
              </button>
            )}
            <button
              type="button"
              onClick={() => setConfirm({ action: "delete", user: row })}
              disabled={busy}
              className="flex items-center gap-1 rounded-md border border-gray-300 px-2 py-1 text-xs text-red-700 hover:bg-red-50 disabled:opacity-50 dark:border-riot-navy-600 dark:text-red-300 dark:hover:bg-riot-navy-700"
            >
              <Trash2 size={12} /> Delete
            </button>
          </div>
        ),
    },
  ];

  return (
    <div className="space-y-4">
      {showInvite && (
        <form
          onSubmit={(e) => void handleInvite(e)}
          className="rounded-lg border border-gray-200 bg-white p-4 dark:border-riot-navy-700 dark:bg-riot-navy-800"
        >
          <div className="mb-3 flex items-start justify-between gap-4">
            <div>
              <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Add user</h3>
              <p className="mt-1 text-xs text-gray-500 dark:text-riot-navy-300">
                Choose the company workspace, assign a role, and send an invitation.
              </p>
            </div>
          </div>

          <div className="grid grid-cols-1 gap-3 lg:grid-cols-[minmax(220px,1fr)_minmax(240px,1.4fr)_180px_auto_auto] lg:items-end">
            <div>
              <label htmlFor="admin-user-company" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Company
              </label>
              <select
                id="admin-user-company"
                value={tenantId}
                onChange={(e) => setTenantId(e.target.value)}
                required
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                <option value="">Select company</option>
                {companies.map((company) => (
                  <option key={company.tenantId} value={company.tenantId}>{company.companyName}</option>
                ))}
              </select>
            </div>
            <div>
              <label htmlFor="admin-user-email" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Email
              </label>
              <input
                id="admin-user-email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                required
                placeholder="user@company.com"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="admin-user-first-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                First name <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="admin-user-first-name"
                type="text"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                placeholder="Pieta"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="admin-user-last-name" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Last name <span className="font-normal text-gray-400">(optional)</span>
              </label>
              <input
                id="admin-user-last-name"
                type="text"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                placeholder="van der Merwe"
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 placeholder:text-gray-400 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
            </div>
            <div>
              <label htmlFor="admin-user-role" className="mb-1 block text-xs font-medium text-gray-700 dark:text-riot-navy-200">
                Role
              </label>
              <select
                id="admin-user-role"
                value={role}
                onChange={(e) => setRole(e.target.value)}
                className="w-full rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                <option value="viewer">Viewer</option>
                <option value="operator">Operator</option>
                <option value="tenant_admin">Tenant admin</option>
              </select>
            </div>
            <button
              type="submit"
              disabled={inviteUser.isPending || !tenantId || !email.trim()}
              className="rounded-md bg-riot-gold-400 px-4 py-2 text-sm font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:cursor-not-allowed disabled:opacity-50"
            >
              {inviteUser.isPending ? "Sending..." : "Send invite"}
            </button>
            <button
              type="button"
              onClick={() => {
                setShowInvite(false);
                setError("");
              }}
              className="rounded-md border border-gray-300 px-4 py-2 text-sm text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
            >
              Cancel
            </button>
          </div>
          {error && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{error}</p>}
        </form>
      )}

      {success && (
        <div className="rounded-md border border-emerald-200 bg-emerald-50 px-4 py-3 text-sm text-emerald-800 dark:border-emerald-500/30 dark:bg-emerald-500/10 dark:text-emerald-200">
          {success}
        </div>
      )}

      {rowError && <p className="text-xs text-red-600 dark:text-red-400">{rowError}</p>}

      {tooManyCompanies && (
        <p role="status" className="text-xs text-gray-500 dark:text-riot-navy-300">
          Select at most {MAX_FILTER_COMPANIES} companies at a time.
        </p>
      )}
      {isError && (
        <p role="alert" className="text-xs text-red-600 dark:text-red-400">
          Couldn&apos;t load users: {extractErrorMessage(listError)}
        </p>
      )}

      <DataTable
        title="All Users"
        totalCount={total}
        columns={columns}
        data={users}
        keyExtractor={(u) => u.userId}
        selectable
        serverSide
        emptyMessage={
          hasMore && users.length === 0 && !isPlaceholderData ? (
            <span>
              No matches in the users checked so far.{" "}
              <button type="button" onClick={showNextPage} className="font-medium text-riot-gold-600 hover:text-riot-gold-700 dark:text-riot-gold-400">
                Keep searching
              </button>
            </span>
          ) : undefined
        }
        filters={{
          searchPlaceholder: "Search users",
          searchMaxLength: MAX_SEARCH_LENGTH,
          searchFields: [
            { value: "email", label: "Email" },
            { value: "name", label: "Name" },
          ],
          // The statuses the backend stores.
          statusOptions: [
            { value: "active", label: "Active" },
            { value: "invited", label: "Invited" },
            { value: "deactivated", label: "Deactivated" },
          ],
          multiSelectFilters: [
            {
              key: "tenantId",
              label: "Companies",
              options: companies.map((company) => ({ value: company.tenantId, label: company.companyName })),
            },
            { key: "role", label: "Roles", options: ROLE_FILTER_OPTIONS },
          ],
        }}
        onFilterChange={(f) =>
          setFilters({
            search: f.search,
            searchField: f.searchField as AdminUserFilters["searchField"],
            status: f.status,
            tenantIds: f.multiSelect.tenantId ?? [],
            roles: f.multiSelect.role ?? [],
          })
        }
        pagination={{
          page: paging.page,
          totalPages: totalPagesFor(paging.page, pageSize, hasMore, total),
          count: users.length,
          hasMore,
          rangeLabel: formatRange(paging.offset, users.length, total, totalIsLowerBound),
          pageSize,
          pageSizeOptions: PAGE_SIZE_OPTIONS,
          onPageSizeChange: setPageSize,
          onNext: showNextPage,
          onPrev: paging.prev,
          onFirst: paging.first,
        }}
        actions={
          <button
            type="button"
            onClick={() => {
              setShowInvite(true);
              setError("");
              setSuccess("");
            }}
            className="flex items-center gap-1.5 rounded-md bg-riot-gold-400 px-3.5 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600"
          >
            <UserPlus size={14} /> Add user
          </button>
        }
      />

      {confirm && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <div role="dialog" aria-modal="true" className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800">
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">
              {confirm.action === "delete" ? "Delete user" : "Disable user"}
            </h3>
            <p className="mt-2 text-xs text-gray-600 dark:text-riot-navy-200">
              {confirm.action === "delete"
                ? `Deleting ${confirm.user.email} disables their sign-in and removes them from the list. Their record is retained for 30 days, then permanently removed — you can't undo this here.`
                : `Disabling ${confirm.user.email} blocks them from signing in until you re-enable them. Their data is kept.`}
            </p>
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setConfirm(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={() => void runConfirmedAction()}
                className={
                  confirm.action === "delete"
                    ? "rounded-md bg-red-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-red-700"
                    : "rounded-md bg-orange-600 px-3 py-1.5 text-xs font-medium text-white hover:bg-orange-700"
                }
              >
                {confirm.action === "delete" ? "Delete" : "Disable"}
              </button>
            </div>
          </div>
        </div>
      )}

      {editing && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
          <form
            onSubmit={(e) => void saveEdit(e)}
            role="dialog"
            aria-modal="true"
            className="w-[min(440px,calc(100vw-2rem))] rounded-lg border border-gray-200 bg-white p-5 shadow-xl dark:border-riot-navy-700 dark:bg-riot-navy-800"
          >
            <h3 className="text-sm font-semibold text-gray-900 dark:text-white">Edit user</h3>
            <div className="mt-4 flex flex-col gap-3">
              <input
                type="text"
                aria-label="Edit first name"
                value={editForm.firstName}
                onChange={(e) => setEditForm((f) => ({ ...f, firstName: e.target.value }))}
                placeholder="First name"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
              <input
                type="text"
                aria-label="Edit last name"
                value={editForm.lastName}
                onChange={(e) => setEditForm((f) => ({ ...f, lastName: e.target.value }))}
                placeholder="Last name"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
              <input
                type="email"
                aria-label="Edit email"
                value={editForm.email}
                onChange={(e) => setEditForm((f) => ({ ...f, email: e.target.value }))}
                placeholder="Email"
                className="rounded-md border border-gray-300 px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              />
              <select
                aria-label="Edit role"
                value={editForm.role}
                onChange={(e) => setEditForm((f) => ({ ...f, role: e.target.value }))}
                className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm text-gray-900 dark:border-riot-navy-600 dark:bg-riot-navy-900 dark:text-white"
              >
                {ROLE_OPTIONS.map((o) => (
                  <option key={o.value} value={o.value}>{o.label}</option>
                ))}
              </select>
            </div>
            {editError && <p className="mt-2 text-xs text-red-600 dark:text-red-400">{editError}</p>}
            <div className="mt-4 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setEditing(null)}
                className="rounded-md border border-gray-300 px-3 py-1.5 text-xs text-gray-700 hover:bg-gray-50 dark:border-riot-navy-600 dark:text-riot-navy-200 dark:hover:bg-riot-navy-700"
              >
                Cancel
              </button>
              <button
                type="submit"
                disabled={updateUser.isPending}
                className="rounded-md bg-riot-gold-400 px-3 py-1.5 text-xs font-medium text-riot-navy-900 hover:bg-riot-gold-500 active:bg-riot-gold-600 disabled:opacity-50"
              >
                {updateUser.isPending ? "Saving..." : "Save"}
              </button>
            </div>
          </form>
        </div>
      )}
    </div>
  );
}
